import { API_BASE_URL } from '../../../../../../config';
import { fetchFamilyInfo, resolveSavedMemberDraft } from '../../family/familyInfoApi';
import type { FamilyMemberApi } from '../../family/familyInfoTypes';
import { parseIncomeYen } from '../spouseSection/spouseEligibility';
import { deriveTaxCategories } from './dependentsSectionSteps';
import type {
  DependentDraft,
  DependentNonResidentReason,
  DisabilityCategory,
  YesNo,
} from './dependentsSectionTypes';

export type DependentCandidate = {
  draft: DependentDraft;
  /** Original family-info payload, round-tripped on save so insurance data is preserved. */
  original: FamilyMemberApi;
};

function boolToYn(v: boolean | null | undefined): YesNo | '' {
  if (v === true) return 'yes';
  if (v === false) return 'no';
  return '';
}

function apiToDependentDraft(m: FamilyMemberApi): DependentDraft {
  const reason = m.nonResidentReason?.trim() ?? '';
  return {
    empFamilyId: m.id ?? null,
    fullName: m.fullName ?? '',
    fullNameKana: m.fullNameKana ?? '',
    relationship: m.relationship ?? '',
    relationshipOther: '',
    dateOfBirth: m.dateOfBirth ?? '',
    myNumber: m.myNumber ?? '',
    livingInSameHousehold: boolToYn(m.livingInSameHousehold),
    ifNotAddress: m.ifNotAddress ?? '',
    // Prefer explicit sharedLivelihood from tax_dependent; fall back for older rows.
    sharedLivelihood:
      m.sharedLivelihood === true
        ? 'yes'
        : m.sharedLivelihood === false
          ? 'no'
          : m.taxEligible === true
            ? 'yes'
            : '',
    estimatedIncome: m.estimatedIncome != null ? String(Math.trunc(Number(m.estimatedIncome))) : '',
    nonResident: boolToYn(m.nonResidentFlag),
    nonResidentReason: (['study_abroad', 'disability', 'remittance_380k', 'none'].includes(reason)
      ? reason
      : '') as DependentNonResidentReason | '',
    remittanceAmount: m.remittanceAmount != null ? String(Math.trunc(Number(m.remittanceAmount))) : '',
    disabilityCategory: (m.disabilityCategory?.trim() as DisabilityCategory) ?? '',
    changeDuringYear: m.taxChangeDate || m.taxChangeReason ? 'yes' : '',
    changeDate: m.taxChangeDate ?? '',
    changeReason: m.taxChangeReason ?? '',
    eligible: m.taxEligible !== false,
    ineligibilityReason: '',
    addedInSection: false,
  };
}

/** Non-spouse family members from Family Info, in stable id order. */
export async function fetchDependentCandidates(
  email: string,
  invitationId?: string
): Promise<DependentCandidate[]> {
  const res = await fetchFamilyInfo(email, invitationId);
  const members = res.members ?? [];
  return members
    .filter((m) => (m.relationship ?? '').toLowerCase() !== 'spouse')
    .map((m) => ({ draft: apiToDependentDraft(m), original: m }));
}

function resolvedRelationship(d: DependentDraft): string {
  if (d.relationship === 'other') return d.relationshipOther.trim() || 'other';
  return d.relationship;
}

function dependentToApi(
  d: DependentDraft,
  original: FamilyMemberApi | null,
  taxYear: number
): FamilyMemberApi {
  const income = parseIncomeYen(d.estimatedIncome);
  const remittance = parseIncomeYen(d.remittanceAmount);
  return {
    // Preserve insurance / other-income blocks captured during Family Info.
    ...(original ?? {}),
    id: d.empFamilyId ?? undefined,
    relationship: resolvedRelationship(d) || undefined,
    fullName: d.fullName.trim() || undefined,
    fullNameKana: d.fullNameKana.trim() || undefined,
    dateOfBirth: d.dateOfBirth || undefined,
    myNumber: d.myNumber.replace(/\D/g, '') || undefined,
    disabilityCategory: d.disabilityCategory || undefined,
    livingInSameHousehold:
      d.livingInSameHousehold === '' ? undefined : d.livingInSameHousehold === 'yes',
    ifNotAddress:
      d.livingInSameHousehold === 'no' ? d.ifNotAddress.trim() || undefined : undefined,
    nonResidentFlag: d.nonResident === '' ? undefined : d.nonResident === 'yes',
    claimTaxDependent: true,
    taxYear,
    estimatedIncome: income > 0 ? income : undefined,
    sharedLivelihood: d.sharedLivelihood === '' ? undefined : d.sharedLivelihood === 'yes',
    taxEligible: d.eligible,
    nonResidentReason: d.nonResident === 'yes' ? d.nonResidentReason || undefined : undefined,
    remittanceAmount:
      d.nonResidentReason === 'remittance_380k' && remittance > 0 ? remittance : undefined,
    taxChangeReason: d.changeDuringYear === 'yes' ? d.changeReason.trim() || undefined : undefined,
    taxChangeDate: d.changeDuringYear === 'yes' ? d.changeDate || undefined : undefined,
    taxCategories: deriveTaxCategories(d, taxYear),
  };
}

/** Upsert one dependent (emp_family + tax_dependent + categories) via the family-info API. */
export async function saveDependent(params: {
  email: string;
  invitationId?: string;
  draft: DependentDraft;
  original: FamilyMemberApi | null;
  taxYear: number;
}): Promise<number | null> {
  if (!API_BASE_URL) return null;
  const payload = {
    email: params.email,
    invitationId: params.invitationId,
    saveMember: dependentToApi(params.draft, params.original, params.taxYear),
  };
  const r = await fetch(`${API_BASE_URL}/api/family-info`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const data = (await r.json()) as { members?: FamilyMemberApi[] };
  if (params.draft.empFamilyId != null) return params.draft.empFamilyId;
  const saved = resolveSavedMemberDraft(
    data.members ?? [],
    {
      id: params.draft.empFamilyId,
      relationship: resolvedRelationship(params.draft),
      fullName: params.draft.fullName,
      dateOfBirth: params.draft.dateOfBirth,
    },
    params.taxYear
  );
  return saved?.id ?? null;
}

/** Resume cursor stored in tax_declaration.current_step_id while in Section 3. */
export function dependentsCursor(processedCount: number): string {
  return `dependents:${processedCount}`;
}

export function parseDependentsCursor(currentStepId: string | null | undefined): number | null {
  const m = (currentStepId ?? '').match(/^dependents:(\d+)$/);
  return m ? Number(m[1]) : null;
}
